import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl-next';
import uuid from 'uuid';
import { ACTION } from '@atlaskit/editor-common/analytics';
import { fg } from '@atlaskit/platform-feature-flags';
import { AgentAvatar } from '@atlaskit/rovo-agent-components';
import { useContextualConversationStarters } from '@atlassian/conversation-assistant';
import { createUnifiedAnalyticsPayload } from '@atlassian/editor-ai-common/analytics/create-unified-analytics-payload';
import { mapToAssistanceServiceProduct } from '@atlassian/editor-ai-common/api/assistance-service';
import { CONFIG_ITEM_KEYS } from '../prebuilt/config-items/config-item-keys';
import { useAnalyticsFlow } from '../utils/analytics/analyticsFlowUtils';
import { useFireAIAnalyticsEvent } from '../utils/analytics/utils';
import { getDummySuggestions } from '../utils/dummy-suggestions';
import { useAIExperienceCommonDataContext } from './useAIExperienceCommonData';
var experienceName = 'Rovo agent conversation starters';
var suggestionCount = 3;
export function useAgentStarters(_ref) {
  var handleHighlight = _ref.handleHighlight,
    configItem = _ref.configItem,
    onConversationStarterSelected = _ref.onConversationStarterSelected,
    product = _ref.product,
    editorPluginAIProvider = _ref.editorPluginAIProvider;
  var agent;
  /**
   * When agent is FREE_GENERATE_EDITOR_AGENT, don't show agent starter.
   * In this case, editor rovo agent is used behind the scenes for free generate.
   */
  if (configItem.key !== CONFIG_ITEM_KEYS.FREE_GENERATE_EDITOR_AGENT) {
    agent = configItem.agent;
  }
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var siteId = useMemo(function () {
    var _editorPluginAIProvid;
    return (editorPluginAIProvider === null || editorPluginAIProvider === void 0 || (_editorPluginAIProvid = editorPluginAIProvider.getChannelVariables) === null || _editorPluginAIProvid === void 0 || (_editorPluginAIProvid = _editorPluginAIProvid.call(editorPluginAIProvider, 'rovo-agent')) === null || _editorPluginAIProvid === void 0 ? void 0 : _editorPluginAIProvid.cloudId) || '';
  }, [editorPluginAIProvider]);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    hasError = _useState2[0],
    setHasError = _useState2[1];
  var fireAIAnalyticsEvent = useFireAIAnalyticsEvent();
  var analyticsFlow = useAnalyticsFlow();
  var _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    lastAiInteractionID = _useState4[0],
    setLastAiInteractionID = _useState4[1];
  var getAiLifeCycleDynamicAttributes = analyticsFlow.getAiLifeCycleDynamicAttributes;
  var userDefinedAgentStarters = useMemo(function () {
    if (!agent) {
      return [];
    }
    return agent.conversationStarters.filter(function (conversationStarter) {
      return conversationStarter.type === 'user-defined';
    });
  }, [agent]);
  var aiExperienceCommonData = useAIExperienceCommonDataContext();
  var onInit = useCallback(function () {
    setHasError(false);
    var aiId = uuid();
    setLastAiInteractionID(aiId);
    fireAIAnalyticsEvent({
      payload: createUnifiedAnalyticsPayload({
        action: ACTION.INITIATED,
        interactionID: aiId,
        experienceName: experienceName,
        isProactive: false,
        invokedFrom: aiExperienceCommonData.lastTriggeredFrom,
        attributes: getAiLifeCycleDynamicAttributes === null || getAiLifeCycleDynamicAttributes === void 0 ? void 0 : getAiLifeCycleDynamicAttributes({
          init: true
        })
      })
    });
  }, [aiExperienceCommonData.lastTriggeredFrom, fireAIAnalyticsEvent, getAiLifeCycleDynamicAttributes]);
  var onError = useCallback(function (error) {
    setHasError(true);
    fireAIAnalyticsEvent({
      payload: createUnifiedAnalyticsPayload({
        action: ACTION.ERROR,
        interactionID: lastAiInteractionID,
        experienceName: experienceName,
        isProactive: false,
        invokedFrom: aiExperienceCommonData.lastTriggeredFrom,
        attributes: _objectSpread({
          aiErrorMessage: error.message,
          aiErrorCode: 500
        }, getAiLifeCycleDynamicAttributes === null || getAiLifeCycleDynamicAttributes === void 0 ? void 0 : getAiLifeCycleDynamicAttributes({
          init: false
        }))
      })
    });
  }, [aiExperienceCommonData.lastTriggeredFrom, fireAIAnalyticsEvent, lastAiInteractionID, getAiLifeCycleDynamicAttributes]);
  var _useContextualConvers = useContextualConversationStarters({
      onInit: onInit,
      onError: onError,
      assistanceServiceParams: {
        touchpointSource: 'editor-ai-conversation-starters',
        product: mapToAssistanceServiceProduct(product),
        experienceId: 'ai-mate',
        siteId: siteId
      }
    }),
    getDynamicContextualStarters = _useContextualConvers.getDynamicContextualStarters,
    contextualStarters = _useContextualConvers.starters,
    isMutating = _useContextualConvers.isMutating;
  var isPending = useMemo(function () {
    return isMutating || contextualStarters.some(function (cs) {
      return !cs;
    }) && userDefinedAgentStarters.length < suggestionCount;
  }, [isMutating, contextualStarters, userDefinedAgentStarters]);
  useEffect(function () {
    if (agent && userDefinedAgentStarters.length < suggestionCount) {
      getDynamicContextualStarters({
        locationUrl: window.location.href,
        fallbackStarters: agent.conversationStarters.map(function (conversationStarter) {
          if (typeof conversationStarter.message !== 'string') {
            conversationStarter.message = formatMessage(conversationStarter.message);
          }
          return conversationStarter;
        }),
        agentNamedId: agent.namedId
      });
    }
  }, [agent, getDynamicContextualStarters, formatMessage, userDefinedAgentStarters]);
  var starters = useMemo(function () {
    if (!agent) {
      return [];
    }
    var conversationStarters = userDefinedAgentStarters;
    if (isPending) {
      return getDummySuggestions(agent, suggestionCount);
    }
    if (conversationStarters.length < suggestionCount) {
      // The linter was being very agressive about this typing for some reason
      // so I had to comfort it and make sure it was aware this variable definitely did
      // not contain nulls.
      // See: How to win friends and influence people by Dale Carnegie
      var nonNullContextualStarters = contextualStarters.filter(function (conversationStarter) {
        return conversationStarter !== null;
      });
      var availableContextualStarters = nonNullContextualStarters.slice(0, suggestionCount - conversationStarters.length);
      conversationStarters = [].concat(_toConsumableArray(conversationStarters), _toConsumableArray(availableContextualStarters));
    }
    return conversationStarters.map(function (_ref2, index) {
      var _agent$externalConfig;
      var conversationStarter = _ref2.message;
      var translatedMessage = typeof conversationStarter === 'string' ? conversationStarter : formatMessage(conversationStarter);
      return _objectSpread({
        value: "agent:".concat(agent.id, ":").concat(index, ":").concat(translatedMessage),
        label: translatedMessage,
        icon: /*#__PURE__*/React.createElement(AgentAvatar, {
          agentId: agent.id,
          agentNamedId: (_agent$externalConfig = agent.externalConfigReference) !== null && _agent$externalConfig !== void 0 ? _agent$externalConfig : agent.namedId,
          agentIdentityAccountId: agent.identityAccountId,
          isForgeAgent: agent.creatorType === 'FORGE',
          forgeAgentIconUrl: agent.icon
        }),
        groupHeading: '',
        onSelect: function onSelect() {
          onConversationStarterSelected(translatedMessage);
        },
        onSuggestionFocused: function onSuggestionFocused() {
          handleHighlight({
            configItem: configItem
          });
        }
      }, fg('ai-smart-button-team-25') && {
        isAgent: true
      });
    });
  }, [agent, isPending, userDefinedAgentStarters, contextualStarters, formatMessage, onConversationStarterSelected, handleHighlight, configItem]);
  useEffect(function () {
    if (!isPending && !hasError && starters.length) {
      fireAIAnalyticsEvent({
        payload: createUnifiedAnalyticsPayload({
          action: ACTION.VIEWED,
          interactionID: lastAiInteractionID,
          experienceName: experienceName,
          isProactive: false,
          invokedFrom: aiExperienceCommonData.lastTriggeredFrom,
          attributes: getAiLifeCycleDynamicAttributes === null || getAiLifeCycleDynamicAttributes === void 0 ? void 0 : getAiLifeCycleDynamicAttributes({
            init: true
          })
        })
      });
    }
  }, [aiExperienceCommonData.lastTriggeredFrom, isPending, hasError, starters, fireAIAnalyticsEvent, lastAiInteractionID, getAiLifeCycleDynamicAttributes]);
  return {
    starters: starters
  };
}